import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "styled-components";

import {
  StrategyControlBtn,
  StrategyControlModalContainer,
} from "./StrategyControl.styles";
import {
  updateStrategyAllocationAmount,
  updateTradeConnection,
} from "features/Strategy/service";

import BrokerModal from "shared/Modal/BrokerModal";
import { toast } from "Layout/slice";
import { tableColumns } from "./tableColumns";
import { getBrokerOAuthURL } from "services";
import { Loader } from "components/Loader";
import { useNavigation } from "utils/navigate";
import StopStrategyModal from "./StopStrategyModal";
import { pauseCircle } from "assets/icons";
import StartStrategyModal from "./StartStrategyModal";
import PauseStrategyModal from "./PauseStrategyModal";
import { SuccessModal } from "shared/Modal/SuccessModal";
import { SnaptradeModal } from "shared/Modal/SnaptradeModal";
import { LimitedOfferModal } from "shared/Modal/LimitedOfferModal";
import MobileStrategyControlCard from "./MobileStrategyControlCard";
import { parseErrorMessage, openNewTab, getBrokerIcon } from "utils";
import { getPortfoliosByStrategyId } from "features/Dashboard/service";
import { AllocationAmountModal } from "shared/Modal/AllocationAmountModal";
import { PrepareUnsubscribeStrategyModal } from "shared/Modal/PrepareUnsubscribeStrategyModal";
import { Table } from "components/Table/index";
import { welcomeWalkthroughSelect } from "Layout/slice/selector";

const StrategyControl = ({
  show = true,
  selectedStrategyData = { name: "", strategy_id: "" },
  ...props
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { navigateToV2PortfolioDetail } = useNavigation();
  const isWelcomeWalkthrough = useSelector(welcomeWalkthroughSelect);

  const [brokerAuthType] = useState("SNAPTRADE");
  const [loading, setLoading] = useState(false);
  const [portfolios, setPortfolios] = useState([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [allocationPrice, setAllocationPrice] = useState(100);
  const [isEditAllocation, setIsEditAllocation] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [showSnaptradeModal, setShowSnaptradeModal] = useState(false);
  const [showStopStrategyModal, setStopStrategyModal] = useState(false);
  const [isBrokerModalVisible, setBrokerModalVisible] = useState(false);
  const [showPauseStrategyModal, setPauseStrategyModal] = useState(false);
  const [showPausedStrategyModal, setPausedStrategyModal] = useState(false);
  const [showStartStrategyModal, setStartStrategyModal] = useState(false);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
  const [showEditAllocation, setShowEditAllocation] = useState(false);
  const [allocationAvailBalance, setAllocationAvailBalance] = useState(100);
  const [showUnsubStrategyModal, setShowUnsubStrategyModal] = useState(false);
  // const [selectAll, setSelectedAll] = useState(false);

  const toggleStrategyControlModal = props.onHide;

  useEffect(() => {
    if (show && selectedStrategyData?.strategy_id) {
      getUserPortfolios();
    }
    return () => {
      setPortfolios([]);
    };
  }, [show]);

  const toggleStartStrategyModal = () => {
    setStartStrategyModal(!showStartStrategyModal);
  };

  const togglePauseStrategyModal = () => {
    setPauseStrategyModal(!showPauseStrategyModal);
  };

  const toggleStopStrategyModal = () => {
    setStopStrategyModal(!showStopStrategyModal);
  };

  const togglePausedStrategyModal = () => {
    setPausedStrategyModal(!showPausedStrategyModal);
  };

  const toggleBrokerModal = () => {
    if (brokerAuthType === "SNAPTRADE") {
      toggleSnaptradeModal();
    }

    // setBrokerModalVisible(!isBrokerModalVisible);
    toggleStrategyControlModal();
  };

  const toggleUnsubscribeStrategyModal = async () => {
    setShowUnsubStrategyModal(!showUnsubStrategyModal);
  };

  const toggleUnsubscribeModal = () => {
    setShowUnsubscribeModal(!showUnsubscribeModal);
  };

  const handleBrokerSelect = async (broker) => {
    try {
      setIsConnecting(true);
      const response = await getBrokerOAuthURL(broker.key);
      if (response?.oauth_url) {
        openNewTab(response.oauth_url);
      } else {
        dispatch(toast({ body: "Account Connected Successfully!" }));
      }
    } catch (error) {
      setIsConnecting(false);
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    } finally {
      setIsConnecting(false);
      setBrokerModalVisible(!isBrokerModalVisible);
    }
  };

  const getUserPortfolios = async () => {
    try {
      setLoading(true);
      let _portfolios = await getPortfoliosByStrategyId(
        selectedStrategyData.strategy_id
      );
      _portfolios = _portfolios.reduce((result, d) => {
        if (d.authentication_status === "confirmed") {
          result.push({
            brokerLogo: getBrokerIcon(d.logo_url, theme.mode),
            selected: false,
            ...d,
          });
        }
        return result;
      }, []);
      const eligibleAccountFirst = _portfolios.sort(
        (a, b) => Number(b.eligible) - Number(a.eligible)
      );
      setPortfolios(eligibleAccountFirst);
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStartStrategy = (portfolio) => {
    setSelectedPortfolio(portfolio);
    if (portfolio.subscription_status === "Not connected") {
      toggleAllocationModal();
      toggleStrategyControlModal();
    } else {
      toggleStartStrategyModal();
    }
  };

  const handlePauseStrategy = (portfolio) => {
    setSelectedPortfolio(portfolio);
    togglePauseStrategyModal();
  };

  const handleStopStrategy = (portfolio) => {
    setSelectedPortfolio(portfolio);
    toggleStopStrategyModal();
  };

  const handleChangeAllocationAmount = (portfolio) => {
    setSelectedPortfolio(portfolio);
    setIsEditAllocation(true);
    toggleAllocationModal();
  };

  const handleManageAllocation = (portfolio) => {
    setSelectedPortfolio(portfolio);
    setShowEditAllocation(!showEditAllocation);
    toggleStrategyControlModal();
  };

  const handleStopClick = () => {
    getUserPortfolios();
    toggleStopStrategyModal();
  };

  const handlePauseClick = () => {
    getUserPortfolios();
    togglePausedStrategyModal();
  };

  const toggleAllocationModal = () => {
    setShowAllocationModal(!showAllocationModal);
    toggleStrategyControlModal();
  };

  const toggleEditAllocationModal = () => {
    setShowEditAllocation(!showEditAllocation);
    toggleStrategyControlModal();
  };

  const handleAllocationContinue = async (price, availableBalance) => {
    setAllocationPrice(price);
    setAllocationAvailBalance(availableBalance);
    setShowAllocationModal(false);
    if (isEditAllocation) {
      try {
        await updateTradeConnection({
          accountId: selectedPortfolio?.broker_account_id,
          newAllocation: {
            [selectedStrategyData.name]: price / availableBalance,
          },
        });
        dispatch(
          toast({
            body: "Allocation amount successfully updated",
          })
        );
      } catch (error) {
        dispatch(
          toast({
            type: "error",
            body: parseErrorMessage(error),
          })
        );
      }
    } else {
      toggleStartStrategyModal();
    }
  };

  const handleEditAllocationContinue = async (strats) => {
    try {
      await updateStrategyAllocationAmount(
        selectedPortfolio.broker_account_id,
        strats
      );
      dispatch(
        toast({
          type: "info",
          body: "Allocations Updated Successfully",
        })
      );
      setShowEditAllocation(false);
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    }
  };

  const handleUnsubscribeNow = () => {
    getUserPortfolios();
  };

  const onStartedStrategy = () => {
    getUserPortfolios();
    toggleStartStrategyModal();
    const options = {
      search: `?activeTab=strategyManagement`,
    };
    navigateToV2PortfolioDetail(selectedPortfolio.broker_account_id, options);
  };

  const toggleSnaptradeModal = () => {
    setShowSnaptradeModal(!showSnaptradeModal);
  };

  const handleSnaptradeSuccess = () => {
    getUserPortfolios();
    toggleStrategyControlModal();
  };

  return (
    <>
      <StrategyControlModalContainer
        show={show}
        onHide={toggleStrategyControlModal}
        size="lg"
      >
        <div className="heading">
          <h2 className="body-heading text-ellipsis">
            {selectedStrategyData.name}
          </h2>
        </div>
        <div className="body-content">
          <div className="desktop-view">
            <Table
              data={portfolios}
              headerClass={
                isWelcomeWalkthrough
                  ? "non-sticky-header"
                  : "strategy-table-header"
              }
              columns={tableColumns(
                handlePauseStrategy,
                handleStopStrategy,
                handleStartStrategy,
                // handleSelectAll,
                // selectAll,
                // onRowCheckboxChange,
                handleChangeAllocationAmount,
                handleManageAllocation,
                theme.mode
              )}
              notFoundText="No portfolio connected"
              isLoading={loading}
              showPagination={false}
            />
          </div>
          <div className="mobile-view">
            {loading && (
              <div className="mb-2">
                <Loader />
              </div>
            )}
            {!loading && portfolios.length === 0 && (
              <div className="no-data-text">No portfolio connected</div>
            )}
            {portfolios.map((portfolio) => (
              <MobileStrategyControlCard
                key={portfolio.broker_account_id}
                portfolio={portfolio}
                handlePauseStrategy={handlePauseStrategy}
                handleStopStrategy={handleStopStrategy}
                handleStartStrategy={handleStartStrategy}
                handleChangeAllocationAmount={handleChangeAllocationAmount}
                handleManageAllocation={handleManageAllocation}
              />
            ))}
          </div>
          <div className="broker-btn-space">
            <StrategyControlBtn
              // className="broker-btn mt-2"
              label={`Connect another broker`}
              onClick={toggleBrokerModal}
            />
          </div>
        </div>
      </StrategyControlModalContainer>
      {showPauseStrategyModal && (
        <PauseStrategyModal
          show={showPauseStrategyModal}
          onHide={togglePauseStrategyModal}
          handlePauseClick={handlePauseClick}
          subscriptionId={selectedPortfolio.subscription_id}
          strategyName={selectedStrategyData.name}
        />
      )}
      {showStopStrategyModal && (
        <StopStrategyModal
          show={showStopStrategyModal}
          onHide={toggleStopStrategyModal}
          handleStopClick={handleStopClick}
          subscriptionId={selectedPortfolio.subscription_id}
          strategyName={selectedStrategyData.name}
        />
      )}
      {showPausedStrategyModal && (
        <SuccessModal
          text="Your strategy has been paused"
          icon={pauseCircle}
          className="pause-strategy-modal"
          onHide={togglePausedStrategyModal}
        />
      )}
      {isBrokerModalVisible && (
        <BrokerModal
          show={isBrokerModalVisible}
          handleBrokerClick={handleBrokerSelect}
          onHide={toggleBrokerModal}
          isConnecting={isConnecting}
        />
      )}
      {showStartStrategyModal && (
        <StartStrategyModal
          show={showStartStrategyModal}
          onHide={toggleStartStrategyModal}
          portfolioId={selectedPortfolio.broker_account_id}
          strategyId={selectedStrategyData.strategy_id}
          allocationPrice={allocationPrice}
          allocationAvailBalance={allocationAvailBalance}
          onStartedStrategy={onStartedStrategy}
          status={selectedPortfolio.subscription_status}
          subscriptionId={selectedPortfolio.subscription_id}
          strategyName={selectedStrategyData.name}
          toggleAllocationModal={setShowAllocationModal}
        />
      )}
      {showAllocationModal && (
        <AllocationAmountModal
          show={showAllocationModal}
          onHide={toggleAllocationModal}
          onContinue={handleAllocationContinue}
          showBackButton={true}
          accountId={selectedPortfolio?.broker_account_id}
          subscriptionId={selectedPortfolio?.subscription_id}
          isEditAllocation={isEditAllocation}
          strategyName={selectedStrategyData?.name}
          portfolioName={selectedPortfolio?.broker_account_nickname}
          portfolioLogo={selectedPortfolio?.logo_url}
          portfolioKey={selectedPortfolio?.broker_key}
          handleBack={toggleAllocationModal}
          showClose={false}
        />
      )}
      {showEditAllocation && (
        <AllocationAmountModal
          showBackButton={true}
          show={showEditAllocation}
          isEditAllocation={true}
          onHide={toggleEditAllocationModal}
          handleBack={toggleEditAllocationModal}
          onContinue={handleEditAllocationContinue}
          accountId={selectedPortfolio?.broker_account_id}
          isEditStrategies={true}
        />
      )}
      {showUnsubscribeModal && (
        <LimitedOfferModal
          show={showUnsubscribeModal}
          onHide={toggleUnsubscribeModal}
          handleCancelSubscription={toggleUnsubscribeStrategyModal}
        />
      )}
      {showUnsubStrategyModal && (
        <PrepareUnsubscribeStrategyModal
          show={showUnsubStrategyModal}
          onHide={toggleUnsubscribeStrategyModal}
          handleUnsubscribeNow={handleUnsubscribeNow}
          subscriptionId={selectedPortfolio.subscription_id}
        />
      )}
      {showSnaptradeModal && (
        <SnaptradeModal
          show={showSnaptradeModal}
          onHide={toggleSnaptradeModal}
          onSuccess={handleSnaptradeSuccess}
        />
      )}
    </>
  );
};

StrategyControl.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  selectedStrategyData: PropTypes.object,
};

export default StrategyControl;
